<template>
  <div class="flex flex-col gap-4">
    <DropdownMenu v-if="buttonOnly && hasConfiguredReviewProcess">
      <DropdownMenuTrigger as-child>
        <Button type="button" size="sm" :variant="setupButtonVariant" :disabled="isCreatingReviewProcess || isRemovingReviewProcess || !canEnableReviewProcess" :loading="isCreatingReviewProcess">
          <span class="inline-flex size-4 items-center justify-center rounded-sm border border-current">
            <Check class="size-3" />
          </span>
          {{ setupButtonLabel }}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" class="w-44">
        <DropdownMenuItem @click="openSetupModal">
          Edit Review
        </DropdownMenuItem>
        <DropdownMenuItem class="text-destructive focus:text-destructive" @click="removeReviewProcess">
          Remove Review
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>

    <Button v-else-if="buttonOnly" type="button" size="sm" :variant="setupButtonVariant" :disabled="isCreatingReviewProcess || isRemovingReviewProcess || !canEnableReviewProcess" :loading="isCreatingReviewProcess" @click="openSetupModal">
      {{ setupButtonLabel }}
    </Button>

    <section v-else-if="activeReviewProcessId === null" class="rounded-lg border bg-card p-6">
      <div class="flex items-center justify-between gap-3">
        <h3 class="text-base font-medium">
          {{ title }}
        </h3>
        <Badge variant="outline">
          Not Configured
        </Badge>
      </div>

      <p class="mt-2 text-sm text-muted-foreground">
        Enable a review process to require final approval before completion.
      </p>

      <Button type="button" size="sm" class="mt-4" :disabled="isCreatingReviewProcess || !canEnableReviewProcess" :loading="isCreatingReviewProcess" @click="openSetupModal">
        Enable Review Process
      </Button>
    </section>

    <ReviewProcessWorkflowCard
      v-else
      :review-process-id="activeReviewProcessId"
      :title="title"
      :mode="mode"
      :hidden-when-inactive="hiddenWhenInactive"
      @updated="onWorkflowUpdated"
      @error="onWorkflowError"
    />

    <ReviewProcessSetupModal
      ref="setupModal"
      :review-process-id="activeReviewProcessId"
      :reviewable-type="reviewableType"
      :reviewable-id="reviewableId"
      @created="onSetupCreated"
      @error="onSetupError"
      @submitting="onSetupSubmitting"
    />
  </div>
</template>

<script setup>
import axios from 'axios'
import { Check } from 'lucide-vue-next'
import { computed, ref, watch } from 'vue'
import { toast } from 'vue-sonner'
import { Badge } from '../../../../../../../resources/js/components/ui/badge/index.js'
import { Button } from '../../../../../../../resources/js/components/ui/button/index.js'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../../../../../../resources/js/components/ui/dropdown-menu/index.js'
import ReviewProcessSetupModal from './ReviewProcessSetupModal.vue'
import ReviewProcessWorkflowCard from './ReviewProcessWorkflowCard.vue'

const props = defineProps({
  reviewProcessId: {
    type: [Number, String],
    default: null,
  },
  reviewableType: {
    type: String,
    required: true,
  },
  reviewableId: {
    type: [Number, String],
    required: true,
  },
  title: {
    type: String,
    default: 'Review Process',
  },
  mode: {
    type: String,
    default: 'auto',
    validator(value) {
      return ['auto', 'compact', 'full'].includes(value)
    },
  },
  hiddenWhenInactive: {
    type: Boolean,
    default: false,
  },
  buttonOnly: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['updated', 'created', 'removed', 'error'])

const activeReviewProcessId = ref(null)
const isCreatingReviewProcess = ref(false)
const isRemovingReviewProcess = ref(false)
const setupModal = ref(null)

const normalizedPropReviewProcessId = computed(() => normalizeId(props.reviewProcessId))
const normalizedReviewableId = computed(() => normalizeId(props.reviewableId))
const hasConfiguredReviewProcess = computed(() => activeReviewProcessId.value !== null)
const setupButtonVariant = computed(() => {
  return hasConfiguredReviewProcess.value ? 'secondary' : 'outline'
})
const setupButtonLabel = computed(() => {
  return hasConfiguredReviewProcess.value ? 'Review Ready' : 'Enable Review'
})

const canEnableReviewProcess = computed(() => {
  return typeof props.reviewableType === 'string'
    && props.reviewableType.trim() !== ''
    && normalizedReviewableId.value !== null
})

watch(
  normalizedPropReviewProcessId,
  (value) => {
    activeReviewProcessId.value = value
  },
  { immediate: true }
)

const openSetupModal = () => {
  setupModal.value?.openModal?.()
}

const onSetupSubmitting = (isSubmitting) => {
  isCreatingReviewProcess.value = isSubmitting === true
}

const onSetupCreated = (payload) => {
  const createdReviewProcessId = normalizeId(payload?.id)

  if (createdReviewProcessId === null) {
    emit('error', { error: null, message: 'Missing review process id in response.' })
    return
  }

  activeReviewProcessId.value = createdReviewProcessId
  emit('created', payload)
  emit('updated', payload)
}

const onSetupError = (payload) => {
  emit('error', payload)
}

const removeReviewProcess = async () => {
  if (activeReviewProcessId.value === null) {
    return
  }

  if (!window.confirm('Remove this review process?')) {
    return
  }

  isRemovingReviewProcess.value = true

  try {
    await axios.delete(`/api/v1/review-processes/${activeReviewProcessId.value}`)
    activeReviewProcessId.value = null
    setupModal.value?.closeModal?.()
    toast.success('Review process removed.')
    emit('removed')
    emit('updated', null)
  } catch (error) {
    const message = readErrorMessage(error, 'Unable to remove review process.')
    toast.error(message)
    emit('error', { error, message })
  } finally {
    isRemovingReviewProcess.value = false
  }
}

const onWorkflowUpdated = (payload) => {
  emit('updated', payload)
}

const onWorkflowError = (payload) => {
  emit('error', payload)
}

function normalizeId(value) {
  const parsed = Number(value)

  if (!Number.isInteger(parsed) || parsed <= 0) {
    return null
  }

  return parsed
}

function readErrorMessage(error, fallbackMessage) {
  return error?.response?.data?.errors?.status?.[0]
    || error?.response?.data?.errors?.reviewable_id?.[0]
    || error?.response?.data?.errors?.reviewable_type?.[0]
    || error?.response?.data?.errors?.final_approvable_by_user_id?.[0]
    || error?.response?.data?.message
    || fallbackMessage
}
</script>
